import React, { useState, useEffect } from "react";
import { base44 } from "@voult/api/base44Client";
import { Key, Plus, Trash2, Copy, Check, Loader2, Eye, EyeOff } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

function generateKey() {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let key = "vk_live_";
  for (let i = 0; i < 32; i++) key += chars[Math.floor(Math.random() * chars.length)];
  return key;
}

export default function IntegrationKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [copied, setCopied] = useState(null);
  const [latestKey, setLatestKey] = useState(null);

  const load = () => {
    setLoading(true);
    base44.entities.IntegrationKey.list("-created_date", 100).then(setKeys).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    const fullKey = generateKey();
    const created = await base44.entities.IntegrationKey.create({
      name: newKeyName.trim(),
      key_prefix: fullKey.slice(0, 12) + "…",
      key_hash: fullKey,
      status: "active",
    });
    await base44.entities.ActivityLog.create({ action: "key_created", entity_type: "IntegrationKey", entity_id: created.id, details: `API key "${newKeyName}" created` });
    setLatestKey({ id: created.id, fullKey });
    setNewKeyName("");
    setShowForm(false);
    setCreating(false);
    load();
  };

  const revoke = async (key) => {
    if (!confirm(`Revoke key "${key.name}"?`)) return;
    await base44.entities.IntegrationKey.update(key.id, { status: "revoked" });
    await base44.entities.ActivityLog.create({ action: "key_revoked", entity_type: "IntegrationKey", entity_id: key.id, details: `API key "${key.name}" revoked` });
    load();
  };

  const copyKey = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1800);
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Integration Keys</h1>
          <p className="text-sm text-slate-500 mt-0.5">API keys for connecting your apps to the tokenization vault.</p>
        </div>
        <button onClick={() => setShowForm((s) => !s)}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 transition">
          <Plus className="h-4 w-4" /> New key
        </button>
      </div>

      {showForm && (
        <div className="mb-5 bg-white rounded-2xl border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-3">Create API Key</h2>
          <div className="flex gap-3">
            <input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="Key name (e.g. Production App)"
              className="flex-1 rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-slate-900" />
            <button onClick={create} disabled={creating || !newKeyName.trim()}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate"}
            </button>
          </div>
        </div>
      )}

      {latestKey && (
        <div className="mb-5 bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
          <p className="text-sm font-semibold text-emerald-800 mb-1">🎉 Your new key — copy it now, it won't be shown again.</p>
          <div className="flex items-center gap-2 mt-2">
            <code className="flex-1 font-mono text-xs bg-white rounded-lg border border-emerald-200 px-3 py-2 break-all text-slate-800">{latestKey.fullKey}</code>
            <button onClick={() => copyKey(latestKey.fullKey, latestKey.id)}
              className="flex-shrink-0 p-2 rounded-lg bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-100">
              {copied === latestKey.id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <button onClick={() => setLatestKey(null)} className="mt-2 text-xs text-emerald-700 hover:underline">I've copied it, dismiss</button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : keys.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <Key className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p>No API keys yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {keys.map((k) => (
            <div key={k.id} className="flex items-center gap-4 px-5 py-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-sm font-medium text-slate-900">{k.name}</p>
                  <span className={`text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 ${k.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{k.status}</span>
                </div>
                <p className="font-mono text-xs text-slate-500">{k.key_prefix}</p>
                {k.last_used && <p className="text-xs text-slate-400 mt-0.5">Last used {formatDistanceToNow(new Date(k.last_used), { addSuffix: true })}</p>}
              </div>
              {k.status === "active" && (
                <button onClick={() => revoke(k)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-2.5 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 transition">
                  <Trash2 className="h-3.5 w-3.5" /> Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}